import autobind from "autobind-decorator";

import {ArrayUtils} from "../../Utils/ArrayUtils";
import {ObjectUtils} from "../../Utils/ObjectUtils";

export class BaseModel {
    constructor() {
    }

    getPrimaryKeyFieldDef() {
        // TODO: cache this to improve performance
        let field = this.fieldDefs.find((x) => x.isPrimaryKey == true);

        if(field) {
            return field;
        } else {
            return null;
        }
    }

    getPrimaryKeyName() {
        let field = this.getPrimaryKeyFieldDef();

        if(field && field.key) {
            return field.key;
        } else {
            return null;
        }
    }

    getPrimaryKeyValue(data) {
        let field = this.getPrimaryKeyFieldDef();

        if(field && field.getter) {
            return field.getter(data);
        } else {
            return null;
        }
    }

    //abstract
    get associations() {
        return [];
    }

    //abstract
    get fieldDefs() {
        return [];
    }

    getAssociationFieldDefs(association) {
        if(association.type === "ArrayOfChildren" && !association.isOne) {
            return [
                {
                    title: association.itemTitle,
                    key: association.name,
                    displayLabel: association.displayLabel,
                    displayOrder: association.displayOrder,
                    requiresPermission: association.requiresPermission,
                    isPrimaryKey: false,
                    isHidden: association.isHidden,
                    group: association.group,
                    groupTitle: association.groupTitle,
                    getter: (x) => x[association.name],
                    setter: (x, y) => x[association.name] = y,
                    editor: 'ChildrenFieldEditor',
                    viewer: 'ChildrenFieldViewer',
                    renderer: 'ChildModelViewerRecordRenderer',
                    model: association.model,
                    association: association,
                    validators: []
                }
            ];
        }

        // make sure we don't modify the fieldDefs
        // array which is shared among all model instances
        let result = ObjectUtils.DeepClone(association.model.getFlattenedFieldDefs());

        let maxOrder = Math.max.apply(null, result.map((x) => x.displayOrder || 0)) + 1;

        result = ObjectUtils.DeepClone(result);

        result.forEach((x) => {
            x.originalKey = x.key;
            x.key = association.name + "_" + x.originalKey;
            x.keyPrefix = association.name + "_" + (x.keyPrefix || "");

            x.group = association.group;
            x.displayOrder = (association.displayOrder || 0) + ((x.displayOrder || 0) / maxOrder);

            if(x.association == undefined) {
                x.associationName = association.name;
                x.model = association.model;
            }

            x.association = {
                ...association,
                association: x.association,
                associationName: x.associationName
            };

            if(
                (association.type === "ArrayOfChildren" && association.isOne) ||
                (association.type === 'ParentPointer')
            ) {
                let oldGetter = x.getter;
                x.getter = (data) => {
                    if(!data[association.name]) {
                        return null;
                    }

                    return oldGetter(data[association.name]);
                };
                let oldSetter = x.setter;
                x.setter = (data, value) => {
                    if(!data[association.name]) {
                        data[association.name] = (association.isOne) ? {} : [];
                    }
                    return oldSetter(data[association.name], value)
                };
            }
        });

        //if the fk is nullable, or if we have a list of children that reduces to a single entry.
        if( association.isNullable &&
            ((association.type === 'ParentPointer') || (association.type === "ArrayOfChildren" && association.isOne))
        ) {
            result.unshift(
                {
                    title: "Has " + association.itemTitle,
                    key: "has_" + association.name,
                    displayLabel: association.displayLabel,
                    displayOrder: association.displayOrder,
                    isPrimaryKey: false,
                    isHidden: association.isHidden,
                    editorOnly: true,
                    group: association.group,
                    getter: (x) => x[association.name] != null,
                    setter: (x, y) => {
                        if(x[association.name] != null) {
                            x[association.name] = null;
                        } else {
                            x[association.name] = {};
                        }
                    },
                    editor: 'BooleanEditor',
                    viewer: 'BooleanViewer',
                    renderer: 'StandardModelViewerRecordRenderer',
                    validators: [],
                    customData: {},
                    association: association,
                    model: association.model,
                    isNullableAssociationFieldToggle: true
                }
            );
        }

        return result;
    }

    @autobind
    getFlattenedFieldDefs(parentAssociation = null) {

    	// this both caches the flattened field defs
	    // and provides a mutable reference to return
    	if(this._flattendFieldDefs) {
    		return this._flattendFieldDefs;
	    }

    	// make sure we don't modify the fieldDefs
        // array which is shared among all model instances
        let result = ObjectUtils.DeepClone(this.fieldDefs);

        this.associations
            .forEach((x) => {
                let children = this.getAssociationFieldDefs(x);
                result = result.concat(children);
            });

        return this._flattendFieldDefs = result;
    }

    @autobind
    getFieldDefGroups() {

	    // this both caches the flattened field defs
	    // and provides a mutable reference to return
	    if(this._fieldDefGroups && 0) {
		    return this._fieldDefGroups;
	    }

        // this gives us back an array of { key: `fieldDef.group`, value: `arrayOfFieldDefs` } objects
        let groups = ArrayUtils.GroupBy(
            this.getFlattenedFieldDefs(),
            (x) => x.group,
            (a, b) => { //order group by first displayOrder, which should be the lowest
                if(a.value.length && b.value.length) {
                    return a.value[0].displayOrder - b.value[0].displayOrder;
                } else {
                    return a.group.localeCompare(b.group);
                }
            }
        );

        // TODO: move this inside the groupKeyLogic in ArrayUtils.GroupBy above?
        // that would improve performance but probably be less readable
        groups.forEach((group) => {
            let groupFields = group.value;
            group.groupTitle = groupFields.reduce(
                (carry, current) => current.groupTitle ?? carry
            , group.key);
        });

        return this._fieldDefGroups = groups;
    }

}
